<script>
import { mapActions } from 'vuex'
import managementMixin from '@/modules/Management/managementMixin'
export default {
  name: 'ManagementPartner',
  components: {
    ContentHeader: () => import('@/components/general/ContentHeader'),
    Action: () => import('@/components/general/Action'),
    FilterList: () => import('@/components/general/FilterList'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Datatable: () => import('@/components/general/Datatable'),
    Pagination: () => import('@/components/general/Pagination'),
    Dropdown: () => import('@/components/general/Dropdown'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    Tabs: () => import('@/components/general/Tabs'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm')
  },

  mixins: [ managementMixin ],

  data () {
    return {
      partnerList: null,
      pagination: {
        query: {},
        filter: {},
        order: {
          active: 'desc',
          name: 'asc'
        },

        page: 1,
        limit: 12,
        lastPage: 3
      },

      search: null,
      modalRemoveOpen: false,
      sessionId: null
    }
  },

  computed: {
    canReadPartners () {
      return [
        'read',
        'write'
      ].indexOf(this.getContextPermission('partners')) !== -1
    },

    canWrite () {
      return this.getContextPermission('partners') === 'write'
    },

    modalDescription () {
      return 'Tem certeza que deseja inativar esse parceiro? Ao desativar um parceiro e tiver conteúdos com usuários matriculados, deverá ser desativado a possibilidade de novas vendas/matrículas'
    }
  },

  watch: {
    'pagination.page' () {
      this.fetchData()
    }
  },

  created () {
    if (this.$route.name !== 'management.partners') {
      return
    }

    this.fetchData()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetPartnerBySlug',
      'attemptListPartners',
      'attemptTogglePartner'
    ]),

    createNewPartner () {
      this.$router.push({ name: 'management.partners.edit' })
    },

    edit (id) {
      this.$router.push({
        name: 'management.partners.edit',
        params: { id: id }
      })
    },

    toggle () {
      this.setFetching(true)

      this.attemptTogglePartner(this.sessionId).then(({ data }) => {
        if (data.active) {
          this.setFeedback({ message: this.$t('management.partner:feedback.activated.success') })
        } else {
          this.setFeedback({ message: this.$t('management.partner:feedback.deactivated.success') })
        }

        this.modalRemoveOpen = false

        this.fetchData()
      }).catch(() => {
        this.setFeedback({ message: this.$t('management.partner:feedback.activated.desactivated.error') })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    searchPartner (search) {
      this.search = search

      this.pagination.query = {
        name: search
      }

      this.pagination.page = 1
      this.fetchData()
    },

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    fetchData () {
      this.setFetching(true)

      this.attemptListPartners(this.pagination)
        .then(({ data }) => {
          this.partnerList = data.data || []
          this.pagination.lastPage = data.lastPage
        })
        .catch(() => {
          this.partnerList = []
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    confirmRemove (item) {
      this.sessionId = item.id

      if (item.active) {
        this.modalRemoveOpen = true

        return
      }

      this.toggle()
    },

    cancelRemove () {
      this.modalRemoveOpen = false
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('management:header.title')"
      :description="$t('management:header.description')"
      background="/assets/images/themes/default/png/management_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="availableTabLink"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        type="button"
        primary
        large
        fixed-width
        :text="$t('management.partner:btn.add')"
        @click="createNewPartner"
      />

      <FilterListSearch
        slot="search"
        hide-error-details
        @search="searchPartner"
      />
    </FilterList>
    <div class="center management-content">
      <Datatable
        v-if="partnerList && partnerList.length > 0"
        :items="partnerList"
      >
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('management.partner.list:datatable.header.1') }}
            </th>
            <th class="th">
              {{ $t('management.partner.list:datatable.header.2') }}
            </th>
            <th class="th">
              {{ $t('management.partner.list:datatable.header.3') }}
            </th>
            <th class="th">
              {{ $t('management.partner.list:datatable.header.4') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ props.item.description }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ props.item.commissionRate }}%</span>
            </td>
            <td class="td">
              <span
                v-if="props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('management.list:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('management.list:inactive') }}</span>
            </td>
            <td
              v-if="canWrite || canReadPartners"
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id)"
                />
                <DropdownLink
                  v-if="!props.item.canceled && canWrite"
                  :text="props.item.active ? $t('global:deactivate') : $t('global:activate')"
                  @click="confirmRemove(props.item)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        v-if="partnerList && partnerList.length > 0"
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />

      <div
        v-else
        class="no-data"
      >
        <h4>Você ainda não possui parceiros cadastrados.</h4>
      </div>
      <RouterView />
    </div>
    <ModalConfirm
      :active="modalRemoveOpen"
      :title="$t('management.partner:deactivate.title')"
      :description="modalDescription"
      @confirmAction="toggle"
      @cancelAction="cancelRemove"
    />
  </div>
</template>

<style src="@/assets/styles/themes/default/management.css"></style>

<style>
.management-content .td:first-child {
  max-width: 280px;
  padding-right: 20px;
}
</style>
